import { useState, useEffect } from 'react';
import { X, Mail, Phone, MapPin, Wrench } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';

interface ServiceProvider {
  id: string;
  provider_id: string;
  name: string;
  service_type: string;
  email: string;
  phone: string;
  phone_ext?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface ViewServiceProviderModalProps {
  providerId: string;
  onClose: () => void;
  onEdit: () => void;
}

export function ViewServiceProviderModal({ providerId, onClose, onEdit }: ViewServiceProviderModalProps) {
  const toast = useToast();
  const [provider, setProvider] = useState<ServiceProvider | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProvider();
  }, [providerId]);

  async function loadProvider() {
    try {
      const { data, error } = await supabase
        .from('service_providers')
        .select('*')
        .eq('id', providerId)
        .single();

      if (error) throw error;
      setProvider(data);
    } catch (error) {
      console.error('Error loading provider:', error);
      toast.error('Failed to load service provider details');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full p-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!provider) {
    return null;
  }

  const InfoRow = ({ label, value, icon: Icon }: { label: string; value?: string; icon?: any }) => {
    if (!value) return null;
    
    return (
      <div className="flex items-start gap-3 py-2">
        {Icon && <Icon className="w-5 h-5 text-slate-400 mt-0.5" />}
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-500">{label}</p>
          <p className="text-base text-slate-900">{value}</p>
        </div>
      </div>
    );
  };

  const fullAddress = [
    provider.street,
    provider.city && provider.state && provider.zip ? `${provider.city}, ${provider.state} ${provider.zip}` : null
  ].filter(Boolean).join('\n');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <Wrench className="w-8 h-8" />
              <div>
                <h2 className="text-2xl font-bold">{provider.name}</h2>
                <p className="text-sm text-blue-100 mt-1">
                  Provider #{provider.provider_id}
                </p>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Service Type Badge */}
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-700">
              {provider.service_type}
            </span>
          </div>

          {/* Contact Information */}
          <div className="bg-slate-50 rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold text-slate-900 border-b border-slate-200 pb-2">
              Contact Information
            </h3>
            <div className="space-y-3">
              <InfoRow label="Email" value={provider.email} icon={Mail} />
              <InfoRow 
                label="Phone" 
                value={provider.phone ? 
                  `${provider.phone}${provider.phone_ext ? ` ext. ${provider.phone_ext}` : ''}` 
                  : undefined
                } 
                icon={Phone} 
              />
            </div>
          </div>

          {/* Address */}
          {fullAddress && (
            <div className="bg-slate-50 rounded-lg p-6 space-y-4">
              <h3 className="text-lg font-semibold text-slate-900 border-b border-slate-200 pb-2">
                Address
              </h3>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-slate-900 whitespace-pre-line">{fullAddress}</p>
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          {provider.notes && (
            <div className="bg-slate-50 rounded-lg p-6 space-y-4">
              <h3 className="text-lg font-semibold text-slate-900 border-b border-slate-200 pb-2">
                Notes
              </h3>
              <p className="text-slate-700 whitespace-pre-wrap">{provider.notes}</p>
            </div>
          )}

          {/* Metadata */}
          <div className="bg-slate-50 rounded-lg p-6 space-y-2">
            <h3 className="text-lg font-semibold text-slate-900 border-b border-slate-200 pb-2">
              Record Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Created</p>
                <p className="text-slate-900 font-medium">
                  {new Date(provider.created_at).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-slate-500">Last Updated</p>
                <p className="text-slate-900 font-medium">
                  {new Date(provider.updated_at).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2.5 border border-slate-300 rounded-lg hover:bg-white transition-colors font-medium"
          >
            Close
          </button>
          <button
            onClick={onEdit}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
          >
            Edit Provider
          </button>
        </div>
      </div>
    </div>
  );
}
